import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Heart, Lock, Globe, User, Eye, EyeOff, KeyRound } from "lucide-react";
import { useT } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import api, { setToken } from "@/lib/api";
import Brand from "@/components/Brand";

export default function Login() {
  const navigate = useNavigate();
  const { t, lang, setLang } = useT();
  const { login, refresh } = useAuth();
  const [username, setUsername] = useState(() => localStorage.getItem("hct_remember_username") || "");
  const [remember, setRemember] = useState(() => !!localStorage.getItem("hct_remember_username"));
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resetMode, setResetMode] = useState(null); // {token, name} after OTP login
  const [newPw, setNewPw] = useState("");
  const [newPw2, setNewPw2] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const result = await login(username.trim(), password);
      if (result?.reset_required) {
        setResetMode({ token: result.reset_token, name: result.user?.full_name || username.trim() });
        setPassword("");
        return;
      }
      if (remember) {
        localStorage.setItem("hct_remember_username", username.trim());
      } else {
        localStorage.removeItem("hct_remember_username");
      }
      // Send user to module chooser (they'll be routed to their preferred tracker).
      // We reset the last choice on each login so they consciously re-pick.
      localStorage.removeItem("hct_module");
      navigate("/choose");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : t("login_error_generic"));
    } finally {
      setLoading(false);
    }
  };

  const submitNewPassword = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await api.post("/auth/force-change-password",
        { new_password: newPw, new_password_confirm: newPw2 },
        { headers: { Authorization: `Bearer ${resetMode.token}` } });
      setToken(res.data.token);
      await refresh();
      localStorage.removeItem("hct_module");
      navigate("/choose");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : t("login_error_generic"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-pattern px-4 py-6" data-testid="login-page">
      {/* Prominent language toggle — top corner */}
      <button
        onClick={() => setLang(lang === "ar" ? "en" : "ar")}
        className="fixed top-4 end-4 z-50 inline-flex items-center gap-1.5 bg-white/95 backdrop-blur border border-teal-200 shadow-lg rounded-full px-3 py-1.5 text-xs font-bold text-teal-800 hover:bg-teal-50 hover:border-teal-400 transition"
        data-testid="lang-toggle-login-top"
        title={t("language")}
        aria-label={t("language")}
      >
        <Globe size={13} /> {lang === "ar" ? "English" : "عربي"}
      </button>

      <div className="w-full max-w-md animate-fade-up">
        <Brand variant="hero" title={t("app_title")} subtitle={t("login_subtitle")} className="mb-5 sm:mb-7" />

        <div className="bg-white rounded-2xl card-shadow p-5 sm:p-7">
          {resetMode ? (
          <form onSubmit={submitNewPassword} className="space-y-5" data-testid="force-change-form">
            <div className="text-center space-y-1">
              <span className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-amber-50 text-amber-600 mb-1"><KeyRound size={22} /></span>
              <h2 className="font-black text-slate-800">{t("fcp_title")}</h2>
              <p className="text-xs text-slate-500">{resetMode.name} — {t("fcp_subtitle")}</p>
            </div>
            <div>
              <label className="inp-label flex items-center gap-2"><Lock size={14} /> {t("fcp_new_password")}</label>
              <input type={showPw ? "text" : "password"} value={newPw} onChange={(e) => setNewPw(e.target.value)}
                placeholder="••••••••" className="inp" style={{ direction: "ltr" }} autoFocus data-testid="fcp-new-password" />
            </div>
            <div>
              <label className="inp-label flex items-center gap-2"><Lock size={14} /> {t("fcp_confirm_password")}</label>
              <input type={showPw ? "text" : "password"} value={newPw2} onChange={(e) => setNewPw2(e.target.value)}
                placeholder="••••••••" className="inp" style={{ direction: "ltr" }} data-testid="fcp-confirm-password" />
            </div>
            <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none">
              <input type="checkbox" checked={showPw} onChange={(e) => setShowPw(e.target.checked)} className="w-3.5 h-3.5 accent-teal-700 cursor-pointer" data-testid="fcp-show-password" />
              {t("show_password")}
            </label>
            {error && (
              <div className="status-expired pill px-3 py-2 w-full justify-center" data-testid="fcp-error">{error}</div>
            )}
            <button type="submit" disabled={loading || !newPw || !newPw2} className="btn btn-primary w-full justify-center py-3" data-testid="fcp-submit">
              {loading ? t("saving") : t("fcp_submit")}
            </button>
          </form>
          ) : (
          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="inp-label flex items-center gap-2">
                <User size={14} /> {t("username")}
              </label>
              <input
                type="text"
                autoFocus={!username}
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder={t("username_placeholder")}
                className="inp"
                style={{ direction: "ltr" }}
                data-testid="login-username-input"
              />
            </div>
            <div>
              <label className="inp-label flex items-center gap-2">
                <Lock size={14} /> {t("password")}
              </label>
              <div className="relative">
                <input
                  type={showPw ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="inp pe-10"
                  style={{ direction: "ltr" }}
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute top-1/2 -translate-y-1/2 end-2 text-gray-400 hover:text-gray-600 p-1"
                  tabIndex={-1}
                  data-testid="login-show-password"
                  title={showPw ? t("hide_password") : t("show_password")}
                  aria-label={showPw ? t("hide_password") : t("show_password")}
                >
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <div className="mt-2 flex items-center justify-between gap-2">
                <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none" data-testid="remember-username-label">
                  <input
                    type="checkbox"
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                    className="w-3.5 h-3.5 accent-teal-700 cursor-pointer"
                    data-testid="remember-username-checkbox"
                  />
                  {t("remember_username")}
                </label>
                <Link
                  to="/forgot-password"
                  className="text-xs text-teal-700 hover:underline font-semibold"
                  data-testid="forgot-password-link"
                >
                  {t("forgot_password")}
                </Link>
              </div>
            </div>

            {error && (
              <div className="status-expired pill px-3 py-2 w-full justify-center" data-testid="login-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !username || !password}
              className="btn btn-primary w-full justify-center py-3"
              data-testid="login-submit-button"
            >
              {loading ? t("logging_in") : t("login")}
            </button>
          </form>
          )}

          <div className="text-center mt-5 text-sm text-gray-600">
            {t("no_account")}{" "}
            <Link to="/register" className="text-teal-700 font-semibold hover:underline" data-testid="goto-register-link">
              {t("create_account")}
            </Link>
          </div>
        </div>

        <p className="text-center text-xs text-gray-400 mt-6 flex items-center justify-center gap-1">
          <Heart size={12} className="text-rose-400" />
          {t("footer_tagline")}
        </p>

        <div className="flex justify-center mt-5">
          <button
            onClick={() => setLang(lang === "ar" ? "en" : "ar")}
            className="btn btn-secondary"
            data-testid="lang-toggle-login"
            title={t("language")}
          >
            <Globe size={14} /> {lang === "ar" ? "English" : "عربي"}
          </button>
        </div>
      </div>
    </div>
  );
}
